import React, { useEffect, useRef, useState } from 'react';

// Tramos de parpadeo detectados en mascota.mp4 (medidos por oscuridad de la
// zona de ojos, ver commits anteriores). El scrub nunca debe caer ahí -- si
// el mouse se queda quieto justo en uno de estos instantes, la mascota se
// congela con los ojos cerrados.
const BLINK_WINDOWS = [
  [1.3, 1.7],
  [2.95, 3.35],
  [4.15, 4.55],
  [5.7, 6.25],
  [7.3, 7.7],
  [9.65, 10],
];

// Construye los tramos "seguros" (complemento de BLINK_WINDOWS en [0, duration])
// y devuelve una función que mapea progreso (0..1) a un tiempo de video que
// jamás cae dentro de un parpadeo.
const buildSafeTimeMapper = (duration, blinkWindows) => {
  const safe = [];
  let cursor = 0;
  blinkWindows.forEach(([start, end]) => {
    if (start > cursor) safe.push([cursor, start]);
    cursor = Math.max(cursor, end);
  });
  if (cursor < duration) safe.push([cursor, duration]);

  const total = safe.reduce((acc, [s, e]) => acc + (e - s), 0);

  return (progress) => {
    let target = progress * total;
    for (let i = 0; i < safe.length; i += 1) {
      const [start, end] = safe[i];
      const len = end - start;
      if (target <= len || i === safe.length - 1) {
        return start + Math.min(target, len);
      }
      target -= len;
    }
    return 0;
  };
};

// Mascota "ORBI": video real (no autoplay) que se scrubbea con la posición
// horizontal del mouse -- scrub continuo, no discreto como una tira de
// frames. Se dibuja en un <canvas> para poder quitarle el fondo blanco en
// vivo (mp4 no soporta transparencia), así flota igual que las imágenes.
const MascotVideo = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const targetTimeRef = useRef(0);
  const pendingSeekRef = useRef(false);
  const mapProgressRef = useRef(null);
  const [ready, setReady] = useState(false);

  // El scrub con mouse no tiene sentido en touch, y capturar frames de video
  // en un <canvas> es poco confiable en varios navegadores móviles (queda en
  // blanco). Por eso en mobile se usa una imagen estática ya recortada, y el
  // video interactivo solo se monta (y descarga) desde md en adelante.
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // El video tiene bastante margen vacío alrededor del personaje; se recorta
  // esa franja para que llene el cuadro y quede pegado al borde derecho.
  const CROP = { sx: 260, sy: 10, sw: 560, sh: 598 };

  const drawFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.videoWidth === 0) return;

    if (canvas.width !== CROP.sw) {
      canvas.width = CROP.sw;
      canvas.height = CROP.sh;
    }
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, CROP.sx, CROP.sy, CROP.sw, CROP.sh, 0, 0, canvas.width, canvas.height);

    const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = frame.data;
    for (let i = 0; i < d.length; i += 4) {
      const minC = Math.min(d[i], d[i + 1], d[i + 2]);
      if (minC > 245) {
        d[i + 3] = 0;
      } else if (minC > 200) {
        const t = (minC - 200) / 45;
        d[i + 3] = Math.round(d[i + 3] * (1 - t));
      }
    }
    ctx.putImageData(frame, 0, 0);
  };

  useEffect(() => {
    if (!isDesktop) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    const handleLoaded = () => setReady(true);
    video.addEventListener('loadedmetadata', handleLoaded);
    return () => video.removeEventListener('loadedmetadata', handleLoaded);
  }, [isDesktop]);

  useEffect(() => {
    if (!isDesktop || !ready) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    mapProgressRef.current = buildSafeTimeMapper(video.duration, BLINK_WINDOWS);

    const handleSeeked = () => {
      drawFrame();
      const desired = targetTimeRef.current;
      if (Math.abs(desired - video.currentTime) > 0.03) {
        video.currentTime = desired;
      } else {
        pendingSeekRef.current = false;
      }
    };
    video.addEventListener('seeked', handleSeeked);

    const handleMove = (e) => {
      const progress = Math.min(1, Math.max(0, e.clientX / window.innerWidth));
      targetTimeRef.current = mapProgressRef.current(progress);
      if (!pendingSeekRef.current) {
        pendingSeekRef.current = true;
        video.currentTime = targetTimeRef.current;
      }
    };
    window.addEventListener('mousemove', handleMove);

    // fuerza un seek inicial: recién cargado el video no tiene un frame
    // decodificado todavía, drawImage() dibujaría un canvas vacío sin esto.
    pendingSeekRef.current = true;
    video.currentTime = 0.001;

    return () => {
      video.removeEventListener('seeked', handleSeeked);
      window.removeEventListener('mousemove', handleMove);
    };
  }, [isDesktop, ready]);

  return (
    <div
      className="relative mx-auto mb-2 md:absolute md:mx-0 md:mb-0 md:bottom-0 md:right-0 z-[2] pointer-events-none select-none w-[46vw] max-w-[170px] sm:max-w-[210px] md:w-[min(36vw,520px)] md:max-w-none"
    >
      {isDesktop ? (
        <>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            ref={videoRef}
            src="/assets/mascota.mp4"
            muted
            playsInline
            preload="auto"
            className="hidden"
          />
          <canvas
            ref={canvasRef}
            className="w-full h-auto"
            style={{ filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.2))' }}
          />
        </>
      ) : (
        <img
          src="/assets/mascota-hero-static.webp"
          alt=""
          className="w-full h-auto"
          style={{ filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.2))' }}
        />
      )}
    </div>
  );
};

export default MascotVideo;
